import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { cn } from '@/lib/utils';

/**
 * Text underline sub-nav for the patient route. Routes and labels are
 * unchanged; the filled icon-pill strip was hard to scan with 9 tabs.
 */
export type PatientTab = {
  to: string;
  label: string;
  emotionsOverride?: string;
};

type Props = {
  patientId: number;
  tabs: PatientTab[];
  /** Override label for the Emotions tab (Digestion vs Wellbeing). */
  emotionsLabel?: string;
};

export const PatientTabNav = ({ patientId, tabs, emotionsLabel }: Props) => {
  const { t } = useTranslation();
  return (
    <nav
      role="tablist"
      aria-label={t('Patient sections')}
      className="-mx-1 flex gap-1 overflow-x-auto border-b px-1"
    >
      {tabs.map((tab) => {
        const label =
          tab.to === 'emotions' && emotionsLabel ? emotionsLabel : tab.label;
        const isNested = tab.to === 'meal-plans';
        return (
          <NavLink
            key={tab.to}
            to={`/patients/${patientId}/${tab.to}`}
            role="tab"
            end={!isNested}
            onClick={() => track(ANALYTICS_EVENTS.TAB_CHANGED, { tab: tab.to })}
            className={({ isActive }) =>
              cn(
                'shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )
            }
          >
            {t(label)}
          </NavLink>
        );
      })}
    </nav>
  );
};
